<template>
  <div class="pi-export-screen">
    <button type="button" class="pi-btn pi-btn--ghost pi-export-screen__btn" :disabled="disabled || !count"
            :aria-busy="busy ? 'true' : 'false'" :aria-disabled="busy ? 'true' : null" :title="count ? null : nothingText" @click="run">
      {{ busy ? "Exportando…" : label }}
    </button>
    <span class="pi-export-screen__notice" :class="{ 'pi-sr': !statusText }" role="status" aria-live="polite">{{ statusText }}</span>
    <span class="pi-export-screen__notice is-error" :class="{ 'pi-sr': !alertText }" role="alert">{{ alertText }}</span>
  </div>
</template>

<script>
import swal from "sweetalert";
import {
  EXPORT_REGISTRY_KEY, browserLink, browserUserName, exportErrorMessage, exportScreen, screenNotice, screenPartialQuestion,
} from "./piExport.js";

const NOTICE_MS = 8000;

const NOTHING_TEXT = "Nenhuma tabela desta tela pode ser exportada agora.";

const confirmDialog = (title, text, accept) => swal({ title: title, text: text, buttons: ["Cancelar", accept] }).then((value) => value === true);

const titleOf = (entry) => (entry && typeof entry.title === "function" ? entry.title() : "");

const confirmOf = (entry) => (entry && typeof entry.confirm === "function" ? entry.confirm() : "");

const enabled = (entry) => !(entry && typeof entry.disabled === "function" && entry.disabled());

export default {
  name: "pi-export-screen",
  inject: {
    exportRegistry: { from: EXPORT_REGISTRY_KEY, default: null },
  },
  props: {
    screen: { type: String, required: true },
    filters: { type: Array, default: () => [] },
    period: { type: Object, default: null },
    label: { type: String, default: "Exportar tela (.xlsx)" },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      count: this.exportRegistry ? this.exportRegistry.count() : 0,
      busy: false,
      nothingText: NOTHING_TEXT,
      notice: "",
      noticeError: false,
    };
  },
  computed: {
    statusText() {
      return this.noticeError ? "" : this.notice;
    },
    alertText() {
      return this.noticeError ? this.notice : "";
    },
  },
  created() {
    if (this.exportRegistry) {
      this.stopCount = this.exportRegistry.subscribe((count) => {
        this.count = count;
      });
    }
  },
  beforeDestroy() {
    if (this.stopCount) this.stopCount();
    this.clearTimer();
  },
  methods: {
    clearTimer() {
      if (this.noticeTimer) clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    },
    showNotice(text, error) {
      this.clearTimer();
      this.notice = text;
      this.noticeError = error === true;
      if (!this.noticeError) {
        this.noticeTimer = setTimeout(() => {
          this.notice = "";
          this.noticeTimer = null;
        }, NOTICE_MS);
      }
    },
    confirmStart(entries) {
      const texts = entries.map(confirmOf).filter((text, index, list) => !!text && list.indexOf(text) === index);
      if (!texts.length) return Promise.resolve(true);
      return confirmDialog("Confirmar exportação", texts.join(" "), "Exportar");
    },
    loadAll(entries) {
      return entries.reduce((chain, entry) => chain.then((tables) => Promise.resolve()
        .then(() => entry.load())
        .then((table) => tables.concat([table]), (error) => {
          const failure = new Error("tabela");
          failure.cause = error;
          failure.title = titleOf(entry);
          throw failure;
        })), Promise.resolve([]));
    },
    run() {
      if (this.busy || !this.exportRegistry) return Promise.resolve(null);
      const entries = this.exportRegistry.entries().filter(enabled);
      if (!entries.length) {
        this.showNotice(NOTHING_TEXT, false);
        return Promise.resolve(null);
      }
      this.busy = true;
      this.clearTimer();
      this.notice = "";
      return this.confirmStart(entries)
        .then((accepted) => (accepted ? this.loadAll(entries) : null))
        .then((tables) => {
          if (!tables) return null;
          const question = screenPartialQuestion(tables);
          if (!question) return tables;
          return confirmDialog("Exportação parcial", question, "Baixar").then((accepted) => (accepted ? tables : null));
        })
        .then((tables) => {
          if (!tables) return null;
          const result = exportScreen({
            screen: this.screen,
            tables: tables,
            filters: this.filters,
            period: this.period,
            link: browserLink(this.$route),
            generatedBy: browserUserName(),
            now: new Date(),
          });
          this.showNotice(screenNotice(result.tables.length, result.exportedRows), false);
          this.$emit("exported", { fileName: result.fileName, tables: result.tables, exportedRows: result.exportedRows, truncated: result.truncated });
          return result;
        })
        .catch((error) => {
          const cause = error && error.cause ? error.cause : error;
          const message = exportErrorMessage(cause, error && error.title ? error.title : "");
          this.showNotice(message, true);
          this.$emit("error", { message: message });
          return null;
        })
        .then((result) => {
          this.busy = false;
          return result;
        });
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-export-screen {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
}

.pi-export-screen__btn {
  white-space: nowrap;
}

.pi-export-screen__btn[aria-disabled="true"] {
  opacity: 0.6;
  cursor: progress;
}

.pi-export-screen__btn:focus {
  outline: 2px solid #1b59f8;
  outline-offset: 2px;
}

.pi-export-screen__notice {
  max-width: 340px;
  font-size: 12px;
  color: #0f6b2b;
}

.pi-export-screen__notice.is-error {
  color: #a3172b;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piExportScreen.vue